import React, { useState, useEffect } from 'react'
import { X, Save, Shield, AlertTriangle } from 'lucide-react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { roleService } from '../../services/roleService'
import { menuService } from '../../services/menuService'
import { MenuItem } from '../../services/menuService'
import toast from 'react-hot-toast'

interface RoleFormData {
  name: string
  description: string
  isActive: boolean
  permissions: {
    all: boolean
    users: {
      create: boolean
      read: boolean
      update: boolean
      delete: boolean
    }
    roles: {
      create: boolean
      read: boolean
      update: boolean
      delete: boolean
    }
    menus: {
      create: boolean
      read: boolean
      update: boolean
      delete: boolean
    }
    cruds: {
      create: boolean
      read: boolean
      update: boolean
      delete: boolean
    }
    ai: {
      access: boolean
      manage: boolean
    }
  }
  menuAccess: {
    dashboard: boolean
    users: boolean
    roles: boolean
    menus: boolean
    cruds: boolean
    aiAssistant: boolean
    configuration: boolean
  }
}

import { Role } from '../../types/auth'

interface RoleFormModalProps {
  isOpen: boolean
  onClose: () => void
  role?: Role | null
}

const RoleFormModal: React.FC<RoleFormModalProps> = ({ isOpen, onClose, role }) => {
  const queryClient = useQueryClient()
  const isEditing = !!role
  
  // Cargar menús para la sección de accesos
  const { data: menuData } = useQuery({
    queryKey: ['menus-roles'],
    queryFn: () => menuService.getMenus(),
    enabled: isOpen, // Solo cargar cuando el modal esté abierto
    staleTime: 5 * 60 * 1000,
  })

  const [selectedMenuIds, setSelectedMenuIds] = useState<number[]>([])

  // Utilidades para manejar el árbol de menús (dentro del componente)
  const findNodeById = (items: MenuItem[] | undefined, id: number): MenuItem | null => {
    if (!items) return null
    for (const item of items) {
      if (item.id === id) return item
      const found = findNodeById(item.children, id)
      if (found) return found
    }
    return null
  }

  const getPathToNode = (items: MenuItem[] | undefined, targetId: number): number[] => {
    if (!items) return []
    for (const item of items) {
      if (item.id === targetId) return [item.id]
      const childPath = getPathToNode(item.children, targetId)
      if (childPath.length) return [item.id, ...childPath]
    }
    return []
  }

  const collectDescendantsFromNode = (node: MenuItem | null): number[] => {
    if (!node || !node.children) return []
    const res: number[] = []
    const stack = [...(node.children || [])]
    while (stack.length) {
      const n = stack.shift()!
      res.push(n.id)
      if (n.children && n.children.length) stack.push(...n.children)
    }
    return res
  }

  // Recolecta todos los IDs del árbol (para "Seleccionar todo")
  const collectAllIds = (items?: MenuItem[]): number[] => {
    if (!items) return []
    const ids: number[] = []
    const stack = [...items]
    while (stack.length) {
      const n = stack.shift()!
      ids.push(n.id)
      if (n.children && n.children.length) stack.push(...n.children)
    }
    return ids
  }

  const toggleMenuSelection = (menuId: number, checked: boolean) => {
    setSelectedMenuIds((prev: number[]) => {
      const set = new Set(prev)
      if (checked) {
        // Seleccionar el nodo y sus ancestros
        const path = getPathToNode(menuData, menuId) // [root..menuId]
        for (const id of path) set.add(id)
      } else {
        // Deseleccionar el nodo y todos sus descendientes
        set.delete(menuId)
        const node = findNodeById(menuData, menuId)
        const descendants = collectDescendantsFromNode(node)
        for (const id of descendants) set.delete(id)
      }
      return Array.from(set)
    })
  }

  const renderMenuItem = (item: MenuItem, level: number = 0): React.ReactNode => {
    const isChecked = selectedMenuIds.includes(item.id)
    const descendants = collectDescendantsFromNode(item)
    const hasChildSelected = descendants.some(id => selectedMenuIds.includes(id))
    const isIndeterminate = !isChecked && hasChildSelected

    return (
      <div key={item.id} className="mb-1">
        <div className="flex items-center" style={{ paddingLeft: level * 16 }}>
          <input
            type="checkbox"
            checked={isChecked}
            ref={(el) => { if (el) el.indeterminate = isIndeterminate }}
            aria-checked={isIndeterminate ? 'mixed' : isChecked}
            onChange={(e) => toggleMenuSelection(item.id, e.target.checked)}
            className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
          />
          <span className="ml-2 text-sm text-gray-800">{item.label || item.name}</span>
        </div>
        {item.children && item.children.length > 0 && (
          <div className="mt-1">
            {item.children.map(child => renderMenuItem(child, level + 1))}
          </div>
        )}
      </div>
    )
  }
  
  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    description: '',
    isActive: true,
    permissions: {
      all: false,
      users: { create: false, read: false, update: false, delete: false },
      roles: { create: false, read: false, update: false, delete: false },
      menus: { create: false, read: false, update: false, delete: false },
      cruds: { create: false, read: false, update: false, delete: false },
      ai: { access: false, manage: false }
    },
    menuAccess: {
      dashboard: false,
      users: false,
      roles: false,
      menus: false,
      cruds: false,
      aiAssistant: false,
      configuration: false
    }
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Reset form when modal opens/closes or role changes
  useEffect(() => {
    if (isOpen && role) {
      setFormData({
        name: role.name,
        description: role.description || '',
        isActive: role.isActive,
        permissions: {
          all: role.permissions?.all || false,
          users: {
            create: role.permissions?.users?.create || false,
            read: role.permissions?.users?.read || false,
            update: role.permissions?.users?.update || false,
            delete: role.permissions?.users?.delete || false,
          },
          roles: {
            create: role.permissions?.roles?.create || false,
            read: role.permissions?.roles?.read || false,
            update: role.permissions?.roles?.update || false,
            delete: role.permissions?.roles?.delete || false,
          },
          menus: {
            create: role.permissions?.menus?.create || false,
            read: role.permissions?.menus?.read || false,
            update: role.permissions?.menus?.update || false,
            delete: role.permissions?.menus?.delete || false,
          },
          cruds: {
            create: role.permissions?.cruds?.create || false,
            read: role.permissions?.cruds?.read || false,
            update: role.permissions?.cruds?.update || false,
            delete: role.permissions?.cruds?.delete || false,
          },
          ai: {
            access: role.permissions?.ai?.access || false,
            manage: role.permissions?.ai?.manage || false,
          }
        },
        menuAccess: {
          dashboard: true, // Por defecto todos pueden ver dashboard
          users: role.permissions?.users?.read || false,
          roles: role.permissions?.roles?.read || false,
          menus: role.permissions?.menus?.read || false,
          cruds: role.permissions?.cruds?.read || false,
          aiAssistant: role.permissions?.ai?.access || false,
          configuration: role.permissions?.all || false
        }
      })
      setSelectedMenuIds(role.menuIds || [])
    } else if (isOpen) {
      setFormData({
        name: '',
        description: '',
        isActive: true,
        permissions: {
          all: false,
          users: { create: false, read: false, update: false, delete: false },
          roles: { create: false, read: false, update: false, delete: false },
          menus: { create: false, read: false, update: false, delete: false },
          cruds: { create: false, read: false, update: false, delete: false },
          ai: { access: false, manage: false }
        },
        menuAccess: {
          dashboard: true, // Dashboard por defecto visible
          users: false,
          roles: false,
          menus: false,
          cruds: false,
          aiAssistant: false,
          configuration: false
        }
      })
      setSelectedMenuIds([])
    }
    setErrors({})
  }, [isOpen, role])

  // Normaliza selección inicial de menús al editar (incluye ancestros de cada menú seleccionado)
  useEffect(() => {
    if (!isOpen || !role || !menuData || !role.menuIds || role.menuIds.length === 0) return
    const set = new Set<number>()
    for (const id of role.menuIds) {
      const path = getPathToNode(menuData, id)
      for (const p of path) set.add(p)
    }
    setSelectedMenuIds(Array.from(set))
  }, [isOpen, role, menuData])

  const createRoleMutation = useMutation({
    mutationFn: roleService.createRole,
    onSuccess: () => {
      queryClient.invalidateQueries(['roles'])
      toast.success('Rol creado exitosamente')
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.message || 'Error al crear rol')
    }
  })

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      roleService.updateRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['roles'])
      toast.success('Rol actualizado exitosamente')
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.message || 'Error al actualizar rol')
    }
  })

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido'
    } else if (formData.name.length < 2) {
      newErrors.name = 'El nombre debe tener al menos 2 caracteres'
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La descripción es requerida'
    }

    // Verificar que al menos tenga algún permiso si no es "all"
    if (!formData.permissions.all) {
      const hasAnyPermission = 
        Object.values(formData.permissions.users).some(p => p) ||
        Object.values(formData.permissions.roles).some(p => p) ||
        Object.values(formData.permissions.menus).some(p => p) ||
        Object.values(formData.permissions.cruds).some(p => p) ||
        Object.values(formData.permissions.ai).some(p => p)
      
      if (!hasAnyPermission) {
        newErrors.permissions = 'El rol debe tener al menos un permiso'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    const submitData = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      isActive: formData.isActive,
      permissions: formData.permissions,
      menuIds: selectedMenuIds
    }

    if (isEditing && role) {
      updateRoleMutation.mutate({ id: role.id, data: submitData })
    } else {
      createRoleMutation.mutate(submitData)
    }
  }

  const handleInputChange = (field: keyof RoleFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handlePermissionChange = (module: string, action: string, value: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: {
          ...(prev.permissions[module as keyof typeof prev.permissions] as Record<string, boolean>),
          [action]: value
        }
      }
    }))
    
    if (errors.permissions) {
      setErrors(prev => ({ ...prev, permissions: '' }))
    }
  }

  const handleAllPermissionsChange = (value: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        all: value,
        // Si se activa "all", desactivar permisos específicos para evitar confusión
        ...(value ? {
          users: { create: false, read: false, update: false, delete: false },
          roles: { create: false, read: false, update: false, delete: false },
          menus: { create: false, read: false, update: false, delete: false },
          cruds: { create: false, read: false, update: false, delete: false },
          ai: { access: false, manage: false }
        } : {})
      }
    }))
    
    if (errors.permissions) {
      setErrors(prev => ({ ...prev, permissions: '' }))
    }
  }

  const isSubmitting = createRoleMutation.isLoading || updateRoleMutation.isLoading

  if (!isOpen) return null

  const modules = [
    { key: 'users', label: 'Usuarios', icon: '👥' },
    { key: 'roles', label: 'Roles', icon: '🛡️' },
    { key: 'menus', label: 'Menús', icon: '📱' },
    { key: 'cruds', label: 'CRUDs', icon: '⚙️' },
    { key: 'ai', label: 'IA', icon: '🤖', actions: ['access', 'manage'] }
  ]

  const crudActions = ['create', 'read', 'update', 'delete']
  const actionLabels = {
    create: 'Crear',
    read: 'Leer',
    update: 'Actualizar',
    delete: 'Eliminar',
    access: 'Acceso',
    manage: 'Gestionar'
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
              <Shield className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditing ? 'Editar Rol' : 'Crear Nuevo Rol'}
              </h2>
              <p className="text-sm text-gray-500">
                {isEditing ? 'Modifica los permisos y datos del rol' : 'Define permisos y accesos para el nuevo rol'}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(90vh - 180px)' }}>
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Información Básica</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre del Rol *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`input w-full ${errors.name ? 'border-red-300' : ''}`}
                    placeholder="Administrador, Editor, etc."
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado
                  </label>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                    />
                    <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                      Rol activo
                    </label>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={2}
                  className={`input w-full ${errors.description ? 'border-red-300' : ''}`}
                  placeholder="Describe las responsabilidades y alcance de este rol"
                />
                {errors.description && (
                  <p className="mt-1 text-xs text-red-600">{errors.description}</p>
                )}
              </div>
            </div>

            {/* Permissions */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Permisos y Accesos</h3>
              
              {/* Super Admin Option */}
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mr-2" />
                    <div>
                      <span className="font-medium text-amber-800">Super Administrador</span>
                      <p className="text-sm text-amber-700">Acceso total a todas las funcionalidades del sistema</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.permissions.all}
                    onChange={(e) => handleAllPermissionsChange(e.target.checked)}
                    className="rounded border-gray-300 text-amber-600 shadow-sm focus:border-amber-300 focus:ring focus:ring-amber-200 focus:ring-opacity-50"
                  />
                </div>
              </div>

              {/* Module Permissions */}
              {!formData.permissions.all && (
                <div className="space-y-4">
                  {modules.map((module) => (
                    <div key={module.key} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <span className="text-lg mr-2">{module.icon}</span>
                        <h4 className="font-medium text-gray-900">{module.label}</h4>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {(module.actions || crudActions).map((action) => (
                          <label key={action} className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(formData.permissions as any)[module.key][action]}
                              onChange={(e) => handlePermissionChange(module.key, action, e.target.checked)}
                              className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              {actionLabels[action as keyof typeof actionLabels]}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {errors.permissions && (
                <p className="mt-2 text-xs text-red-600 flex items-center">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {errors.permissions}
                </p>
              )}
            </div>

            {/* Menu Access Configuration - dinámico por árbol */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">📱 Asignación de Menús (canView)</h3>
              <p className="text-sm text-gray-600 mb-3">
                Marca los menús que este rol podrá ver. Si seleccionas un submenú, su(s) padre(s) se seleccionarán automáticamente.
              </p>

              {!menuData && (
                <div className="text-sm text-gray-500">Cargando menús...</div>
              )}
              {menuData && menuData.length === 0 && (
                <div className="text-sm text-gray-500">No hay menús disponibles.</div>
              )}
              {menuData && menuData.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <button
                      type="button"
                      className="btn btn-secondary-static btn-xs"
                      onClick={() => setSelectedMenuIds(collectAllIds(menuData))}
                      disabled={!menuData || menuData.length === 0}
                    >
                      Seleccionar todo
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary-static btn-xs"
                      onClick={() => setSelectedMenuIds([])}
                      disabled={selectedMenuIds.length === 0}
                    >
                      Limpiar selección
                    </button>
                    <span className="ml-auto text-xs text-gray-500">
                      {selectedMenuIds.length} seleccionado(s)
                    </span>
                  </div>
                  <div className="border border-gray-200 rounded-md p-3 max-h-72 overflow-auto bg-white">
                    {menuData.map(item => renderMenuItem(item, 0))}
                  </div>
                </div>
              )}

              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start">
                  <Shield className="h-4 w-4 text-blue-600 mt-0.5 mr-2" />
                  <div className="text-sm text-blue-800">
                    <strong>Nota:</strong> Asegúrate de seleccionar también los menús padre cuando sea necesario. El sistema incluirá padres automáticamente durante la selección.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form for submission */}
        <form onSubmit={handleSubmit} className="hidden">
          {/* Hidden form for submission */}
        </form>

        {/* Footer */}
        <div className="flex justify-end items-center p-6 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="btn btn-secondary-static btn-md"
            >
              Cancelar
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="btn btn-primary-static btn-md"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="spinner w-4 h-4 mr-2"></div>
                  {isEditing ? 'Actualizando...' : 'Creando...'}
                </div>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {isEditing ? 'Actualizar Rol' : 'Crear Rol'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RoleFormModal
